/**
 * proFeatureRows: the ONE Basic vs Pro list.
 *
 * Source of truth is the owner's "Why go Pro" rows (2026-09-11). Every Pro
 * sales surface in the app renders from here (the paywall, the Pro one-pager,
 * Settings) and the website's pricing table (nexusdex-infopage
 * assets/index.html #compare) carries the same rows by hand. Change a row
 * here and on the site together.
 *
 * A cell is either a boolean (tick / dash) or a short label ("Top 1", "3",
 * "Unlimited", "Backup only", "Preview", "Count only"). Rows free for
 * everyone stay in the list on purpose: Basic must never read as empty.
 */

export type ProCell = boolean | string;

export interface ProFeatureRow {
  key: string;
  title: string;
  /** One line under the title. */
  detail?: string;
  basic: ProCell;
  pro: ProCell;
}

export interface ProFeatureGroup {
  key: string;
  heading: string;
  rows: ProFeatureRow[];
}

export const PRO_FEATURE_GROUPS: ProFeatureGroup[] = [
  {
    key: "pokedex",
    heading: "Pokédex",
    rows: [
      {
        key: "tracking",
        title: "Nexus Dex tracking",
        detail: "Main, Shiny, Lucky, XXS, XXL, Shadow, Purified, Mega, G-Max, Dynamax, Shundo, Shiny Lucky, Regional and Costume",
        basic: true,
        pro: true,
      },
      { key: "liveScanner", title: "Live GO Pokédex scanner", detail: "Unlimited uses per day", basic: true, pro: true },
      {
        key: "pageScans",
        title: "Pokédex and Journal page scans",
        detail: "Screenshots of a Pokédex page or your Journal",
        basic: "Unlimited",
        pro: "Unlimited",
      },
      { key: "scanHistory", title: "Scan history", detail: "Review every entry, with a quick undo", basic: false, pro: true },
      {
        key: "speciesPageScan",
        title: "Live GO species page scan",
        detail: "Update several dex types at the same time",
        basic: false,
        pro: true,
      },
      {
        key: "restore",
        title: "Restore your Nexus Dex and Pokémon",
        detail: "After a reinstall or a new phone. Join Pro later and restore from your backup",
        basic: "Backup only",
        pro: true,
      },
    ],
  },
  {
    key: "pokemon",
    heading: "Pokémon, IVs and move-sets",
    rows: [
      { key: "ivScans", title: "Live Pokémon and IV scans", basic: true, pro: true },
      {
        key: "recommendation",
        title: "Smart Pokémon recommendation",
        detail: "What each one is good for, and what to do with it",
        basic: false,
        pro: true,
      },
    ],
  },
  {
    key: "meta",
    heading: "Your Pokémon vs the current meta (Battle Hub)",
    rows: [
      {
        key: "top10ByType",
        title: "Your Pokémon vs the Top 10 by type",
        detail: "The current meta Top 10 Dragon, Water and every other type, with the move-sets to run",
        basic: "Top 1",
        pro: "Top 10",
      },
      { key: "top100", title: "Your Pokémon vs the full Top 100", detail: "Move-sets included", basic: "Top 1", pro: "Top 10" },
      {
        key: "metaSearch",
        title: "Copy a Pokémon GO search per meta Pokémon",
        detail: "Arm your strongest with the right move-sets in one paste",
        basic: false,
        pro: true,
      },
    ],
  },
  {
    key: "friends",
    heading: "Friends and trades",
    rows: [
      { key: "friends", title: "Friends", basic: "3", pro: "Unlimited" },
      { key: "priorityFriends", title: "Priority friends", detail: "Pin them to the top of your list", basic: false, pro: "3" },
      { key: "tradeMatches", title: "Trade matches per friend", detail: "Across every dex type", basic: "3", pro: "Unlimited" },
      { key: "wishList", title: "Wish List", detail: "7 Pokémon your friends can see", basic: false, pro: true },
    ],
  },
  {
    key: "events",
    heading: "Events",
    rows: [
      {
        key: "eventGaps",
        title: "Your missing Pokémon in every event",
        detail: "Including what your friends are missing",
        basic: "Count only",
        pro: true,
      },
      {
        key: "dynamax",
        title: "Max Battles: your best team and Max coverage",
        detail: "For every Dynamax and Gigantamax boss this rotation, the two hardest hitters and the tank from your own Max copies, the Max Move each fights with, and the 18-type coverage map with the gaps to fill. The bosses and their weaknesses are free",
        basic: "Bosses",
        pro: true,
      },
      {
        key: "rocket",
        title: "Rocket line-ups vs your Nexus Dex",
        detail: "Your best three from your own Pokémon against every line-up, and every shadow this rotation hands over against your Shadow, Purified, Shiny, 100%, Lucky, XXL and XXS dexes. The line-ups themselves are free",
        basic: "Line-ups",
        pro: true,
      },
    ],
  },
  {
    key: "other",
    heading: "Other awesome features",
    rows: [
      { key: "variants", title: "Log caught variants and patterns", detail: "Unown, Vivillon, Spinda and friends", basic: true, pro: true },
      {
        key: "searchStrings",
        title: "Pokémon GO search string creator",
        detail: "Copy, then paste into Pokémon GO",
        basic: "Preview",
        pro: true,
      },
      {
        key: "naming",
        title: "Rename scanned Pokémon as you go",
        detail: "Your naming convention, copied automatically as you scan",
        basic: false,
        pro: true,
      },
      {
        key: "teamColours",
        title: "Team colours for the whole app and icon",
        detail: "Instinct, Valor or Mystic",
        basic: false,
        pro: true,
      },
      { key: "pipeline", title: "Everything we build next", detail: "New features land all the time. Stay tuned", basic: false, pro: true },
    ],
  },
];

/** Rows where Pro gives more than Basic, for the short "what Pro unlocks" lists. */
export function proOnlyRows(): ProFeatureRow[] {
  return PRO_FEATURE_GROUPS.flatMap((g) => g.rows).filter((r) => r.basic !== r.pro);
}

/** The headline pitch, in the owner's order: what most trainers hit first. */
export const PRO_HEADLINE_KEYS = [
  "friends",
  "tradeMatches",
  "top10ByType",
  "scanHistory",
  "restore",
  "naming",
  "teamColours",
] as const;

export function proHeadlineRows(): ProFeatureRow[] {
  const all = PRO_FEATURE_GROUPS.flatMap((g) => g.rows);
  return PRO_HEADLINE_KEYS.map((k) => all.find((r) => r.key === k)).filter((r): r is ProFeatureRow => !!r);
}
